import React, { useState, useEffect, useMemo } from 'react';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    createFilterOptions,
} from '@mui/material';
import { getEmployeeSuggestions, Employee, NotifyRecipient } from '../dataProvider';

/**
 * Sentinel shape injected into the Autocomplete options list when the user's
 * input looks like an e-mail address that doesn't match any known employee.
 * Selecting this option opens the external-recipient confirmation dialog.
 */
interface ExternalEmailPrompt {
    /** Discriminant flag — always `true` for the prompt sentinel. */
    _promptExternal: true;
    email: string;
    employee_id: null;
    full_name: string;
}

/** Union of real autocomplete options and the external-email prompt sentinel. */
type AutocompleteOption = NotifyRecipient | ExternalEmailPrompt;

/** Basic e-mail format check used to decide whether to show the external-address prompt. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const filter = createFilterOptions<AutocompleteOption>();

export interface NotifyOthersInputProps {
    /** Full list of selected recipients. External entries have `employee_id: null`. */
    value: NotifyRecipient[];
    /**
     * Fires with the updated recipient list on every selection change.
     * The list contains full `NotifyRecipient` objects so callers can serialise
     * them without re-fetching.
     */
    onChange: (value: NotifyRecipient[]) => void;
    disabled?: boolean;
}

/**
 * Multi-select autocomplete for the "Notify Others" field on the Apply Timeoff form.
 *
 * **Selection persistence** – selected recipients are stored as full `NotifyRecipient`
 * objects and merged back into the dropdown options on every render, preventing previously
 * chosen chips from disappearing when a new search replaces the API result set.
 *
 * **External e-mail flow**
 * 1. User types a valid e-mail address.
 * 2. If no matching employee is found, a synthetic "Add …" option (identified by
 *    `_promptExternal: true`) is appended to the dropdown via `filterOptions`.
 * 3. Selecting that option opens a MUI `Dialog` for confirmation.
 * 4. Confirming adds `{ email, full_name: email, employee_id: null }` to the selection;
 *    cancelling discards the pending address.
 */
export const NotifyOthersInput: React.FC<NotifyOthersInputProps> = ({
    value,
    onChange,
    disabled = false,
}) => {
    const [options, setOptions] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [pendingEmail, setPendingEmail] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (inputValue.length < 2) {
            setOptions([]);
            return;
        }

        const fetchSuggestions = async () => {
            setLoading(true);
            try {
                const data = await getEmployeeSuggestions(inputValue);
                setOptions(data);
            } catch (error) {
                console.error('Error fetching employee suggestions:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        const debounceTimer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounceTimer);
    }, [inputValue]);

    /**
     * Merge currently selected recipients into the suggestion list so that
     * previously chosen chips remain visible even when a new search replaces `options`.
     */
    const mergedOptions = useMemo<AutocompleteOption[]>(() => {
        const suggestedEmails = new Set(options.map(o => o.email.toLowerCase()));
        const extra = value.filter(v => !suggestedEmails.has((v.email ?? '').toLowerCase()));
        return [...options, ...extra];
    }, [options, value]);

    const handleConfirmExternal = () => {
        if (pendingEmail) {
            onChange([
                ...value,
                { employee_id: null, full_name: pendingEmail, email: pendingEmail },
            ]);
        }
        setDialogOpen(false);
        setPendingEmail(null);
    };

    const handleCancelExternal = () => {
        setDialogOpen(false);
        setPendingEmail(null);
    };

    return (
        <>
            <Autocomplete
                multiple
                options={mergedOptions}
                loading={loading}
                disabled={disabled}
                value={value as AutocompleteOption[]}
                onChange={(_, newValue) => {
                    // Detect if the user selected the external-email prompt sentinel.
                    const typed = newValue as AutocompleteOption[];
                    const lastItem = typed[typed.length - 1];
                    if (lastItem && '_promptExternal' in lastItem && lastItem._promptExternal) {
                        // Open the confirmation dialog; do not commit the value yet.
                        setPendingEmail(lastItem.email);
                        setDialogOpen(true);
                        return;
                    }
                    onChange(typed as NotifyRecipient[]);
                }}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                filterOptions={(opts, params) => {
                    const filtered = filter(opts, params);
                    const { inputValue: input } = params;

                    // Append the external-email prompt when:
                    // • the input matches a basic e-mail pattern,
                    // • no existing suggestion has the same address, and
                    // • the address is not already in the selected list.
                    // All comparisons are case-insensitive to prevent duplicates.
                    const inputLower = input.toLowerCase();
                    const alreadySelected = value.some(v => v.email.toLowerCase() === inputLower);
                    const matchesEmployee = opts.some(
                        o => !('_promptExternal' in o) && o.email.toLowerCase() === inputLower
                    );
                    if (EMAIL_REGEX.test(input) && !matchesEmployee && !alreadySelected) {
                        filtered.push({
                            _promptExternal: true,
                            email: input,
                            employee_id: null,
                            full_name: input,
                        } as ExternalEmailPrompt);
                    }

                    return filtered;
                }}
                getOptionLabel={(option) => {
                    if ('_promptExternal' in option && option._promptExternal) {
                        return `Add "${option.email}" as external recipient`;
                    }
                    // External recipients already added have employee_id === null.
                    if (option.employee_id === null) {
                        return option.email;
                    }
                    return `${option.full_name} (${option.email})`;
                }}
                isOptionEqualToValue={(option, val) => {
                    // The prompt sentinel is never equal to any real value.
                    if ('_promptExternal' in option && option._promptExternal) return false;
                    if (option.employee_id !== null && val.employee_id !== null) {
                        return option.employee_id === val.employee_id;
                    }
                    return option.email === val.email;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Notify Others (Optional)"
                        placeholder="Start typing to search..."
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Confirmation dialog — shown before adding a non-employee e-mail address. */}
            <Dialog open={dialogOpen} onClose={handleCancelExternal}>
                <DialogTitle>Add External Recipient</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>{pendingEmail}</strong> is not a registered employee.
                        Do you want to add this e-mail address as an external notification recipient?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelExternal}>Cancel</Button>
                    <Button onClick={handleConfirmExternal} variant="contained">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
